import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

import { AuthProvider } from "./auth/AuthContext";
import { RequireAuth } from "./auth/RequireAuth";
import { useAuth } from "./auth/useAuth";
import { Alerts } from "./pages/Alerts";
import { Compare } from "./pages/Compare";
import { Categories } from "./pages/Categories";
import { Dashboard } from "./pages/Dashboard";
import { LinkBrokerage } from "./pages/LinkBrokerage";
import { MarginPlanner } from "./pages/MarginPlanner";
import { Watchlist } from "./pages/Watchlist";
import { MarketTargetPage } from "./pages/MarketTarget";
import { NotificationSettingsPage } from "./pages/NotificationSettings";
import { Privacy } from "./pages/Privacy";
import { Button } from "@/components/ui/button";
import { VersionInfo } from "@/components/VersionInfo";
import { cn } from "@/lib/utils";

const NAV_ITEMS = [
  { to: "/", label: "Dashboard", end: true },
  { to: "/watchlist", label: "Watchlist", end: false },
  { to: "/compare", label: "Compare", end: false },
  { to: "/categories", label: "Categories", end: false },
  { to: "/market-target", label: "Market Target", end: false },
  { to: "/alerts", label: "Alerts", end: false },
  { to: "/link", label: "Brokerage", end: false },
  { to: "/margin-plan", label: "Planner", end: false },
  { to: "/settings", label: "Settings", end: false },
];

function HeaderNavigation() {
  const { isAuthenticated, signOut } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu whenever the route changes (a tap on a link navigates).
  useEffect(() => setOpen(false), [location.pathname]);

  const current = NAV_ITEMS.find((item) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to),
  );

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn(
      "whitespace-nowrap border-b-2 px-3 py-3 text-sm font-medium transition-colors",
      isActive
        ? "border-primary text-foreground"
        : "border-transparent text-muted-foreground hover:text-foreground",
    );

  return (
    <>
      <header className="sticky top-0 z-10 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex h-12 max-w-7xl items-center gap-2 px-3 sm:h-14 sm:px-6">
          <Link
            to="/"
            className="shrink-0 text-base font-semibold tracking-tight text-foreground sm:text-lg"
          >
            smart<span className="text-primary">-margin</span>
          </Link>
          {isAuthenticated && (
            <>
              <span className="min-w-0 flex-1 truncate text-center text-xs font-medium text-foreground sm:hidden">
                {current?.label ?? "Menu"}
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-1.5 text-xs sm:ml-auto sm:h-8 sm:px-3 sm:text-sm"
                onClick={signOut}
              >
                Sign out
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 sm:hidden"
                aria-label="toggle-navigation"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
              >
                {open ? <X className="size-4" /> : <Menu className="size-4" />}
              </Button>
            </>
          )}
        </div>
        {isAuthenticated && open && (
          <ul className="mx-auto max-w-7xl border-t border-border px-3 pb-2" data-testid="nav-menu-mobile">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "block rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary/10 text-foreground"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </header>

      {isAuthenticated && (
        <nav className="hidden border-b border-border bg-card/40 sm:block">
          <div className="mx-auto flex max-w-7xl gap-1 px-6">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.end} className={linkClass}>
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </>
  );
}

// Routes whose content benefits from a wider column (dense, multi-column tables).
// Everything else stays at the comfortable reading width.
const WIDE_ROUTES = ["/watchlist", "/compare", "/categories", "/margin-plan"];

function MainContent() {
  const location = useLocation();
  const wide = WIDE_ROUTES.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/"),
  );
  return (
    <main className={cn("mx-auto w-full flex-1 px-6 py-8", wide ? "max-w-7xl" : "max-w-5xl")}>
      <Routes>
        {/* Public: the privacy policy must be viewable without signing in. */}
        <Route path="/privacy" element={<Privacy />} />
        {/* Everything else requires authentication. */}
        <Route
          path="*"
          element={
            <RequireAuth>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/watchlist" element={<Watchlist />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/market-target" element={<MarketTargetPage />} />
                {/* The ladder and change-security flows now live on the Market Target
                    page; keep these paths working for existing links/bookmarks. */}
                <Route path="/ladder" element={<Navigate to="/market-target" replace />} />
                <Route path="/change" element={<Navigate to="/market-target" replace />} />
                <Route path="/alerts" element={<Alerts />} />
                <Route path="/settings" element={<NotificationSettingsPage />} />
                <Route path="/link" element={<LinkBrokerage />} />
                <Route path="/margin-plan" element={<MarginPlanner />} />
              </Routes>
            </RequireAuth>
          }
        />
      </Routes>
    </main>
  );
}

export function App() {
  return (
    <AuthProvider>
      <div className="flex min-h-svh flex-col bg-background text-foreground">
        <HeaderNavigation />
        <MainContent />
        <footer className="border-t border-border/60 px-6 py-3">
          <VersionInfo />
        </footer>
      </div>
    </AuthProvider>
  );
}
